<script setup lang="ts">
// Storage meter in the sidebar: web files plus PrintHQ Vault, against the plan.
import { computed, onMounted, ref, watch } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useVaultStore } from "@/stores/vault";
import { TIER_FEATURES } from "@/services/stripe";
import { formatBytes } from "@/utils/fileKinds";

defineProps<{ collapsed?: boolean }>();

const auth = useAuthStore();
const vault = useVaultStore();
/** Combined usage from the server (vault-api /quota); null until known. */
const server = ref<{ used: number; limit: number } | null>(null);

const planLimit = computed(() => ((TIER_FEATURES[auth.subscriptionTier]?.storageGb || 2) + (auth.storageExtraGb || 0)) * 1024 ** 3);
const used = computed(() => server.value?.used ?? auth.storageUsedBytes + (vault.loaded ? vault.totalBytes : 0));
const limit = computed(() => server.value?.limit || planLimit.value);
const pct = computed(() => (limit.value ? Math.min(100, Math.round((used.value / limit.value) * 100)) : 0));
// Any usage above zero shows at least 1.5% so it stays visible.
const fill = computed(() => {
	if (!limit.value || used.value <= 0) return 0;
	return Math.min(100, Math.max(1.5, (used.value / limit.value) * 100));
});
const tone = computed(() => (pct.value >= 90 ? "danger" : pct.value >= 75 ? "warn" : ""));

async function refresh() {
	if (auth.isDemoMode || !auth.userId) return;
	try {
		const { supabase } = await import("@/services/supabase");
		const { data, error } = await supabase.functions.invoke("vault-api/quota", { method: "GET" });
		if (error || !data) return;
		server.value = { used: Number(data.used_bytes) || 0, limit: Number(data.limit_bytes) || 0 };
	} catch {
		/* keep the local estimate */
	}
}
onMounted(refresh);
// Web uploads/deletes change the profile's counter; re-ask the server then.
watch(() => auth.storageUsedBytes, refresh);
watch(() => vault.objects.length, refresh);
</script>

<template>
	<RouterLink
		v-if="!collapsed"
		to="/files"
		class="block mx-3 mb-2 p-3 rounded-xl border border-line bg-panel hover:border-line-strong transition-colors"
		:aria-label="`Storage: ${formatBytes(used)} of ${formatBytes(limit)} used`"
	>
		<div class="flex items-center justify-between">
			<span class="section-title">Storage</span>
			<span class="text-[11px] text-fg-3 tabular-nums">{{ pct }}%</span>
		</div>
		<div
			class="meter sm mt-2"
			role="meter"
			aria-label="Storage used"
			aria-valuemin="0"
			aria-valuemax="100"
			:aria-valuenow="pct"
			:aria-valuetext="`${pct}% used`"
		>
			<span :class="tone" :style="{ width: `${fill}%` }" />
		</div>
		<p class="mt-1.5 text-[11px] text-fg-3 tabular-nums">{{ formatBytes(used) }} of {{ formatBytes(limit) }}</p>
	</RouterLink>
</template>
